/**
 * Institution Service
 *
 * API calls for institution management (SUPER_ADMIN only)
 */

import { get, post, put, del } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'
import type {
  Institution,
  CreateInstitutionData,
  UpdateInstitutionData,
  InstitutionSearchFilters,
  InstitutionListResponse,
  InstitutionStats,
  GlobalStats,
  CreateInstitutionResponse,
} from '@/domain/institution/types'

/**
 * Get paginated list of institutions
 */
export async function getInstitutions(
  filters?: InstitutionSearchFilters
): Promise<InstitutionListResponse> {
  const params = new URLSearchParams()

  if (filters?.search) params.append('search', filters.search)
  if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive))
  if (filters?.city) params.append('city', filters.city)
  if (filters?.limit) params.append('limit', String(filters.limit))
  if (filters?.offset) params.append('offset', String(filters.offset))

  const queryString = params.toString()
  const url = queryString
    ? `${API_ROUTES.INSTITUTIONS.BASE}?${queryString}`
    : API_ROUTES.INSTITUTIONS.BASE

  return get<InstitutionListResponse>(url)
}

/**
 * Get institution by ID
 */
export async function getInstitutionById(id: string): Promise<Institution> {
  return get<Institution>(API_ROUTES.INSTITUTIONS.BY_ID(id))
}

/**
 * Create new institution with admin user
 */
export async function createInstitution(
  data: CreateInstitutionData
): Promise<CreateInstitutionResponse> {
  return post<CreateInstitutionResponse>(API_ROUTES.INSTITUTIONS.BASE, data)
}

/**
 * Update institution
 */
export async function updateInstitution(
  id: string,
  data: UpdateInstitutionData
): Promise<Institution> {
  return put<Institution>(API_ROUTES.INSTITUTIONS.UPDATE(id), data)
}

/**
 * Delete institution (soft delete)
 */
export async function deleteInstitution(id: string): Promise<void> {
  return del(API_ROUTES.INSTITUTIONS.DELETE(id))
}

/**
 * Activate institution
 */
export async function activateInstitution(id: string): Promise<{ message: string }> {
  return post<{ message: string }>(API_ROUTES.INSTITUTIONS.ACTIVATE(id), {})
}

/**
 * Deactivate institution
 */
export async function deactivateInstitution(id: string): Promise<{ message: string }> {
  return post<{ message: string }>(API_ROUTES.INSTITUTIONS.DEACTIVATE(id), {})
}

/**
 * Get institution statistics
 */
export async function getInstitutionStats(id: string): Promise<InstitutionStats> {
  return get<InstitutionStats>(API_ROUTES.INSTITUTIONS.STATS(id))
}

/**
 * Get global statistics (all institutions)
 */
export async function getGlobalStats(): Promise<GlobalStats> {
  return get<GlobalStats>(API_ROUTES.INSTITUTIONS.GLOBAL_STATS)
}

export type GlobalCatalogStudyType = {
  code: string
  name: string
  description: string | null
  displayOrder: number
}

export type GlobalCatalogPriority = {
  code: string
  name: string
  color: string
  textColor: string
  sortOrder: number
  isDefault: boolean
}

export type GlobalCatalog = {
  studyTypes: GlobalCatalogStudyType[]
  priorities: GlobalCatalogPriority[]
}

/**
 * Get global catalog items available for provisioning
 */
export async function getGlobalCatalog(): Promise<GlobalCatalog> {
  return get<GlobalCatalog>(API_ROUTES.INSTITUTIONS.CATALOG_GLOBAL)
}

export type OnboardData = {
  copyStudyTypes?: boolean
  copyPriorities?: boolean
  studyTypeCodes?: string[]
  priorityCodes?: string[]
}

export type OnboardResult = {
  message: string
  studyTypesCopied: number
  prioritiesCopied: number
  skipped: number
}

/**
 * Provision an institution with global catalog data (study types + priorities)
 */
export async function onboardInstitution(id: string, data?: OnboardData): Promise<OnboardResult> {
  return post<OnboardResult>(API_ROUTES.INSTITUTIONS.ONBOARD(id), data ?? {})
}

/** Módulos que se pueden habilitar o deshabilitar por institución. */
export interface ModuloDelCatalogo {
  codigo: string
  nombre: string
  descripcion: string
  /**
   * Estado cuando la institución no ha dicho nada de este módulo.
   *
   * Lo decide el catálogo del servidor y viaja con él. La regla NO se repite
   * en el cliente: casi todos nacen encendidos, y el del médico remitente no,
   * porque al encenderlo amplía quién puede leer historia clínica.
   */
  porDefecto: boolean
}

export async function obtenerCatalogoDeModulos(): Promise<ModuloDelCatalogo[]> {
  const respuesta = await get<{ modulos: ModuloDelCatalogo[] }>('/institutions/modulos')
  return respuesta.modulos
}
